import { CaptureUpdateAction, newElementWith } from "@excalidraw/excalidraw";
import { dataURLToFile, getDataURL } from "@excalidraw/excalidraw/data/blob";
import { isInitializedImageElement } from "@excalidraw/excalidraw/element/typeChecks";
import { IMAGE_MIME_TYPES } from "@canvaschool/contracts";

import { ApiError } from "../api/client";

import type {
  ExcalidrawElement,
  FileId,
} from "@excalidraw/excalidraw/element/types";
import type {
  BinaryFileData,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import type { SerializedExcalidrawElement } from "@canvaschool/contracts";
import type { CanvaschoolApi } from "../api/client";

export type AssetSyncState = {
  uploading: number;
  failedUploads: number;
  failedDownloads: number;
};

export type AssetSyncOptions = {
  api: Pick<CanvaschoolApi, "uploadAsset" | "fetchAsset">;
  classId: string;
  canvasId: string;
  excalidrawAPI: Pick<
    ExcalidrawImperativeAPI,
    "getSceneElementsIncludingDeleted" | "getFiles" | "updateScene" | "addFiles"
  >;
  /** File IDs the server already stores for this canvas. */
  savedFileIds: readonly string[];
  onChange(state: AssetSyncState): void;
  /** A stored file may release elements that were waiting for it. */
  onFileSaved?(fileId: string): void;
};

export type AssetSync = {
  /** Uploads new local files and downloads stored ones the scene needs. */
  sync(elements: readonly ExcalidrawElement[], files: BinaryFiles): void;
  /** Canonical scenes only reference files the server stores. */
  noteCanonical(elements: readonly SerializedExcalidrawElement[]): void;
  isFileReady(fileId: string): boolean;
  hasPendingUploads(): boolean;
  retry(): void;
  dispose(): void;
};

const isImageMimeType = (value: string): value is BinaryFileData["mimeType"] =>
  (IMAGE_MIME_TYPES as readonly string[]).includes(value);

/**
 * Stores image files through the authorized asset routes. An element that
 * references a file is sent only after the file is stored; peers fetch stored
 * files by ID.
 */
export const createAssetSync = (options: AssetSyncOptions): AssetSync => {
  const { api, classId, canvasId, excalidrawAPI } = options;
  const saved = new Set(options.savedFileIds);
  const uploading = new Set<string>();
  const failedUploads = new Set<string>();
  const downloading = new Set<string>();
  const failedDownloads = new Set<string>();
  let disposed = false;

  const report = () => {
    if (!disposed) {
      options.onChange({
        uploading: uploading.size,
        failedUploads: failedUploads.size,
        failedDownloads: failedDownloads.size,
      });
    }
  };

  /** Uses Excalidraw's own image states; the change is not an undo step. */
  const setStatus = (fileId: string, status: "saved" | "error") => {
    let changed = false;
    const elements = excalidrawAPI
      .getSceneElementsIncludingDeleted()
      .map((element) => {
        if (
          isInitializedImageElement(element) &&
          element.fileId === fileId &&
          !element.isDeleted &&
          element.status !== status
        ) {
          changed = true;
          return newElementWith(element, { status });
        }
        return element;
      });
    if (changed) {
      excalidrawAPI.updateScene({
        elements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
  };

  const upload = async (fileId: string, file: BinaryFileData) => {
    uploading.add(fileId);
    report();
    try {
      await api.uploadAsset(
        classId,
        canvasId,
        fileId,
        dataURLToFile(file.dataURL),
      );
      if (disposed) {
        return;
      }
      saved.add(fileId);
      uploading.delete(fileId);
      setStatus(fileId, "saved");
      // A pasted element may already read "saved", so no scene change follows.
      options.onFileSaved?.(fileId);
    } catch (error) {
      if (disposed) {
        return;
      }
      uploading.delete(fileId);
      // The server derives the file ID from the pre-resize original, but
      // uploads the client's resized re-encode, which isn't guaranteed
      // byte-identical across two resize runs of the same source image
      // (e.g. a retry after a reload). A CONFLICT here means the server
      // already holds *some* asset under this ID from an earlier attempt —
      // functionally saved, even though this run's bytes differ — so
      // treat it as saved rather than surfacing a false "failed" banner.
      if (error instanceof ApiError && error.code === "CONFLICT") {
        saved.add(fileId);
        setStatus(fileId, "saved");
        options.onFileSaved?.(fileId);
        report();
        return;
      }
      failedUploads.add(fileId);
      setStatus(fileId, "error");
    }
    report();
  };

  const download = async (fileId: string) => {
    downloading.add(fileId);
    report();
    try {
      const blob = await api.fetchAsset(classId, canvasId, fileId);
      if (!isImageMimeType(blob.type)) {
        throw new Error("Unsupported image type");
      }
      const dataURL = await getDataURL(blob);
      if (disposed) {
        return;
      }
      const at = Date.now();
      excalidrawAPI.addFiles([
        {
          id: fileId as FileId,
          mimeType: blob.type,
          dataURL,
          created: at,
          lastRetrieved: at,
        },
      ]);
    } catch {
      failedDownloads.add(fileId);
    } finally {
      downloading.delete(fileId);
    }
    report();
  };

  const sync: AssetSync["sync"] = (elements, files) => {
    if (disposed) {
      return;
    }
    const pendingStored: string[] = [];
    for (const element of elements) {
      if (!isInitializedImageElement(element) || element.isDeleted) {
        continue;
      }
      const { fileId } = element;
      const local = files[fileId];
      if (saved.has(fileId)) {
        if (
          !local &&
          !downloading.has(fileId) &&
          !failedDownloads.has(fileId)
        ) {
          void download(fileId);
        } else if (local && element.status === "pending") {
          pendingStored.push(fileId);
        }
      } else if (
        local &&
        !uploading.has(fileId) &&
        !failedUploads.has(fileId)
      ) {
        void upload(fileId, local);
      }
    }
    if (pendingStored.length > 0) {
      // Another upload already stored the same file; outside this change.
      void Promise.resolve().then(() => {
        if (!disposed) {
          pendingStored.forEach((fileId) => setStatus(fileId, "saved"));
        }
      });
    }
  };

  return {
    sync,
    noteCanonical(elements) {
      for (const element of elements) {
        if (
          element.type === "image" &&
          !element.isDeleted &&
          typeof element.fileId === "string"
        ) {
          saved.add(element.fileId);
        }
      }
    },
    isFileReady: (fileId) => saved.has(fileId),
    hasPendingUploads: () => uploading.size > 0 || failedUploads.size > 0,
    retry() {
      failedUploads.clear();
      failedDownloads.clear();
      report();
      sync(
        excalidrawAPI.getSceneElementsIncludingDeleted(),
        excalidrawAPI.getFiles(),
      );
    },
    dispose() {
      disposed = true;
    },
  };
};
